import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import type { POI } from "../types";
import { ITALIAN_REGIONS, countPoiByRegion } from "../lib/regions";

interface RegionPickerModalProps {
  isOpen: boolean;
  pois: POI[];
  onConfirm: (region: string) => void;
}

/**
 * Onboarding modal: appare al primo avvio per chiedere all'utente
 * quale regione vuole esplorare gratuitamente. La scelta e' permanente
 * (storicizzata in localStorage via `useUnlock`).
 *
 * Non ha onClose: l'utente DEVE scegliere una regione per accedere
 * alla mappa. E' un blocco intenzionale (entry-funnel del paywall).
 */
export const RegionPickerModal: React.FC<RegionPickerModalProps> = ({
  isOpen,
  pois,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const counts = useMemo(() => countPoiByRegion(pois), [pois]);
  const regions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ITALIAN_REGIONS;
    return ITALIAN_REGIONS.filter(r => r.name.toLowerCase().includes(q));
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-cyan-900/80 backdrop-blur-md p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="region-picker-title"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <header className="px-6 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <img
              src="/icon-192.png"
              alt="Itinere"
              className="w-12 h-12 rounded-xl shadow-sm flex-shrink-0"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
            <div className="flex-grow">
              <h1
                id="region-picker-title"
                className="text-2xl font-bold text-gray-900 tracking-tight leading-none"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              >
                Benvenuto in Itinere
              </h1>
              <p className="text-xs text-gray-500 mt-1">Tutte le guide in una sola mappa</p>
            </div>
          </div>
          <p className="text-sm text-gray-700 leading-snug">
            Scegli <strong>una regione</strong> da esplorare gratuitamente.
            Potrai sbloccare la mappa completa di tutta Italia con un acquisto unico (€7,99).
          </p>
          <p className="text-xs text-amber-700 mt-2">
            La scelta e' permanente: assicurati di selezionare la regione che ti interessa di piu'.
          </p>
        </header>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-100">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca regione..."
            className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 placeholder-gray-400"
            autoComplete="off"
          />
        </div>

        {/* Lista regioni */}
        <div className="flex-grow overflow-y-auto px-3 py-2">
          {regions.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">Nessuna regione trovata.</p>
          )}
          <ul className="space-y-1">
            {regions.map(r => {
              const count = counts[r.name] || 0;
              const isSelected = selected === r.name;
              return (
                <li key={r.name}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.name)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-baseline justify-between gap-2 ${
                      isSelected
                        ? "bg-cyan-50 ring-2 ring-cyan-500"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <span className={`font-semibold ${isSelected ? "text-cyan-700" : "text-gray-900"}`}>
                      {r.name}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">{count} locali</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer con CTA */}
        <footer className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            disabled={!selected}
            onClick={() => selected && onConfirm(selected)}
            className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors"
          >
            {selected ? `Inizia con ${selected}` : "Seleziona una regione"}
          </button>
          {selected && (
            <p className="text-xs text-center text-gray-500 mt-2">
              La scelta non potra' essere modificata
            </p>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
};
